'use client';

import { useState } from 'react';

import type { Offer } from '@libs/common-types';
import { useLoyaltyContext, useAlertBanner } from '@libs/common-next';
import apiProxyClient from '@libs/api-proxy-client';

export function getCardSize(offer?: Offer, isFeatured?: boolean) {
    if (isFeatured) {
        return 'featured';
    }

    return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}

export function useOfferCardActions() {
    const {
        drawerOpen,
        getOffers,
        isActivating,
        selectedOffer,
        setDrawerOpen,
        setSelectedOffer,
    } = useLoyaltyContext();

    const { setAlert } = useAlertBanner();

    const [activatingOffers, setActivatingOffers] = useState<Set<string>>(
        new Set()
    );
    const [scrollPosition, setScrollPosition] = useState(
        typeof window !== 'undefined' ? window.scrollY : 0
    );

    function handleCardClick(offer: Offer) {
        setDrawerOpen(true);
        setSelectedOffer(offer);
        if (typeof window !== 'undefined') {
            setScrollPosition(window.scrollY);
        }

        // Reset error so pre-existing error doesn't display inside offer drawer
        setAlert(undefined);
    }

    function handleCloseDrawer() {
        setDrawerOpen(false);
        window.scroll(0, scrollPosition);

        // Reset error so error from offer drawer doesn't display on offer grid
        setAlert(undefined);
    }

    async function handleActivateOffer(offer?: Offer, _gtmSection?: string) {
        if (offer && offer.isActivatable && !offer.isActivated) {
            setActivatingOffers(prevOffers =>
                new Set(prevOffers).add(offer.offerId)
            );

            try {
                const response =
                    await apiProxyClient.loyalty.activateOffer(offer);

                if (response?.success) {
                    setAlert(undefined);

                    // Fetch offers again from API to get updated data after activation
                    await getOffers();
                    // optimistic render.  Should match relevant offer in new getOffers array
                    setSelectedOffer({ ...offer, isActivated: true });
                } else {
                    throw Error;
                }
            } catch (error) {
                setAlert({
                    header: 'Uh oh!',
                    text: `[${offer?.shortDescription}] offer didn’t activate. Please try again.`,
                    variant: 'error',
                    icon: 'exclamation-circle-fill',
                });
            } finally {
                setActivatingOffers(prevOffers => {
                    const updatedSet = new Set(prevOffers);
                    updatedSet.delete(offer.offerId);
                    return updatedSet;
                });
            }
        }
    }

    function showSpinner(offer?: Offer) {
        return offer ? activatingOffers.has(offer.offerId) : false;
    }

    return {
        drawerOpen,
        handleActivateOffer,
        handleCardClick,
        handleCloseDrawer,
        isActivating,
        selectedOffer,
        setDrawerOpen,
        setSelectedOffer,
        showSpinner,
    };
}
